import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import moment from 'moment-timezone';

import { TimeFrameKey } from 'data/time-frames';
import { useProducts } from 'hooks/products/useProducts';
import { useAppTheme } from 'hooks/useAppTheme';
import device from 'hooks/useDevice';
import colors from 'styles/colors';
import { GraphData } from 'types/products';
import appHelpers from 'utils/app-urls';
import { formatCurrency } from 'utils/currencies';
import formatNumber from 'utils/format-number';
import hexToRgba from 'utils/hex-to-rgba';

interface ProductProps {
  color: string;
  productCode: string;
  id: string;
  name: string;
  type: string;
}

interface ProductGraphProps {
  productCode: string;
  timeFrame: TimeFrameKey;
  primaryData: GraphData[];
  secondaryData?: GraphData[] | undefined;
  secondaryColor?: string;
  primaryColor: string | undefined;
  secondaryProductCode?: string;
  secondaryProduct?: ProductProps;
  lineWidth: number;
  isRoundIcon?: boolean;
  fiatCode: string;
}

const cdnURL = appHelpers.getCDNUrl();

const ProductGraph = (props: ProductGraphProps) => {
  const {
    productCode,
    timeFrame,
    primaryData,
    secondaryData,
    secondaryColor,
    secondaryProductCode,
  } = props;
  const { products } = useProducts();
  const { isDeviceMobile } = device();

  const { theme } = useAppTheme();

  const [minY, maxY] = (() => {
    const primaryChanges = primaryData ? primaryData.map(d => d.change) : [];
    const secondaryChanges = secondaryData
      ? secondaryData.map(d => d.change)
      : [];
    const allChanges = [...primaryChanges, ...secondaryChanges];
    const min = Math.min(...allChanges);
    const max = Math.max(...allChanges);
    const padding = (max - min) / 6;
    return [min - padding, max + padding];
  })();

  const getProductColor = (code: string) => {
    const product = products?.find(p => p.code === code?.replace('v', ''));
    const color = product?.colors?.secondary || product?.colors?.primary;
    return color || colors?.['vivid-blue'];
  };

  const formatStartDate = () => {
    const { x: value } = primaryData[0] || {};
    const time = moment.unix(value).format('HH:mm');

    const day = moment.unix(value).format('ddd');
    const date = moment.unix(value).format('D');
    const thisMonth = moment.unix(value).format('MMM');

    const month = moment.unix(value).format('MMMM');

    const monthDay = moment.unix(value).format('D');

    const yearMonth = moment.unix(value).format('MMM');
    const year = moment.unix(value).format('YYYY');

    switch (timeFrame) {
      case '24h':
        return `${time} ${day} ${date} ${thisMonth}`;
      case '7d':
        return `${day} ${date} ${thisMonth}`;
      case '1m':
        return `${day} ${monthDay} ${month}`;
      case '3m':
        return `${day} ${monthDay} ${month} ${year}`;
      case '1y':
        return `${yearMonth} ${year}`;
      case 'all':
        return `${yearMonth} ${year}`;
      default:
        return ``;
    }
  };

  const renderTimeStamp = (timestamp: any) => {
    switch (timeFrame) {
      case '24h':
        return moment(timestamp).format('HH:mm A');
      case '7d':
      case '1m':
      case '3m':
      case '1y':
      case 'all':
        return `${moment(timestamp).format('DD/MM/YYYY')} <br/> ${moment(
          timestamp,
        ).format('HH:mm A')}`;
      default:
        return moment(timestamp).format('HH:mm A');
    }
  };

  const bottomMargin = timeFrame === '24h' ? 40 : 60;
  // Use first and last normalised values, i.e graph points, to ensure color matches graph positions
  const graphColor =
    primaryData[0]?.y >= primaryData[primaryData?.length - 1]?.y
      ? theme.error
      : theme.success;

  const intervalRaw = (maxY - minY) / 6;
  const yAxisIntervals = [
    1, 2, 3, 5, 10, 20, 50, 100, 200, 500, 1000, 2000, 3000, 5000, 10000,
  ];
  const yInterval = yAxisIntervals.reduce((prev, curr) => {
    return Math.abs(curr - intervalRaw) < Math.abs(prev - intervalRaw)
      ? curr
      : prev;
  });

  const options = {
    chart: {
      type: 'areaspline',
      showAxes: true,
      backgroundColor: 'transparent',
      margin: isDeviceMobile ? [5, 0, bottomMargin, 0] : undefined,
      height: isDeviceMobile && 300,
    },
    tooltip: {
      useHTML: true,
      followPointer: true,
      borderWidth: 0,
      backgroundColor: '',
      shared: true,

      formatter() {
        const tooltips: string[] = [];

        const self: any = this;
        self.points.forEach((point: any, idx: any) => {
          const pointValue = point.point.value;

          const { change } = point.point;

          const { name } = point.point.series;

          let changeValue;
          if (change < 0) {
            changeValue = `<div
                            style="
                            font-style: normal;
                            font-weight: 500;
                            font-size: 16px;
                            line-height: 21px;
                            letter-spacing: 0.02em;
                            color: ${theme.error};
                            "
                            >
                            ↓ ${change.toFixed(2).replace('-', '')}%*
                            </div>`;
          } else if (change === 0.0) {
            changeValue = `<div
                            style="
                            font-style: normal;
                            font-weight: 500;
                            font-size: 16px;
                            line-height: 21px;
                            letter-spacing: 0.02em;
                            color: ${colors.darkGrey};
                            ">
                            -- 0*
                            </div>`;
          } else {
            changeValue = `<div
                            style="
                            font-style: normal;
                            font-weight: 500;
                            font-size: 16px;
                            line-height: 21px;
                            letter-spacing: 0.02em;
                            color: ${theme.success};"
                            >
                            ↑ ${change.toFixed(2)}%*
                            </div>`;
          }
          const tooltipImage = ` <div
                                    class="${
                                      props.isRoundIcon
                                        ? 'rounded-full'
                                        : 'rounded-full'
                                    } p-0.5 w-10 h-10 justify-center flex flex-row items-center mr-2"
                                    style="
                                        background: ${
                                          name !== 'Series 1'
                                            ? getProductColor(productCode)
                                            : getProductColor(productCode)
                                        };"
                                >

                                    <img src="${
                                      name !== 'Series 1'
                                        ? `${cdnURL}assets/products/icons/${secondaryProductCode?.toLowerCase()}.svg`
                                        : `${cdnURL}assets/products/icons/${productCode.toLowerCase()}.svg`
                                    }" alt="product" class="w-10" />
                                
                            </div>`;

          const tooltipValue = `<div
                        class="text-header-text"
                        style="
                                            font-style: normal;
                                            font-weight: 500;
                                            font-size: 14px;
                                            line-height: 21px;
                                            letter-spacing: 0.02em;"
                    >${formatCurrency(pointValue, props.fiatCode)}</div>`;

          const tooltipDate = `<div
                                                style="
                                                font-style: normal;
                                                font-weight: 500;
                                                font-size: 11px;
                                                line-height: 16px;
                                                letter-spacing: 0.02em;
                                                color: ${colors.lightSteelBlue};"
                                            >
                                                ${renderTimeStamp(point.point.x)}
                                            </div>`;

          const tooltipChangeFrom = `<div
                                style="
                                    font-style: normal;
                                    font-weight: bold;
                                    font-size: 12px;
                                    line-height: 14px;
                                    text-align: ${isDeviceMobile ? 'right' : 'left'};
                                    color: ${colors.lightSteelBlue};
                                    padding-top: 5px;
									white-space: pre-wrap;
                                    ">*% change from ${
                                      isDeviceMobile ? '<br />' : ''
                                    }${formatStartDate()}</div>`;

          tooltips.push(
            isDeviceMobile
              ? `<div>
                                <div class="flex justify-between">
                                    ${tooltipValue}
                                    ${changeValue}
                                </div>
                                <div class="flex justify-between items-end mt-1">
                                    ${tooltipDate}
                                    <div style="margin-right: 2px;">
                                        ${tooltipChangeFrom}
                                    </div>
                                </div>
                            </div>`
              : `
                            <div
                                style="display: flex;
                                flex-direction: row;
                                justify-content: space-between;
                                margin-bottom: 5px;
                                padding-right: 20px;"
                            >
                            ${!isDeviceMobile ? tooltipImage : ``}
                                <div style="display: flex; flex-direction: column; width: 140px;">
                                    ${changeValue}

                                    ${tooltipValue}
                                    ${idx === self.points.length - 1 ? tooltipDate : ``}
                                </div>
                            </div>
                            ${tooltipChangeFrom}
                    `,
          );
        });

        return `<div
                            style="
                                backdrop-filter: blur(4px);
                                box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
                                min-height: ${!isDeviceMobile && '80px'};
                                padding: 10px;
                                ${
                                  timeFrame === '24h' || isDeviceMobile
                                    ? `width: 235px;`
                                    : `width: 195px;`
                                }
                                font-family: Bozon, serif;"
                                class="bg-background rounded-lg"
                            >
                            ${tooltips.join('')}
                            </div>`;
      },
    },
    plotOptions: {
      areaspline: {
        marker: {
          enabled: false,
        },
        lineWidth: props.lineWidth,
        states: {
          hover: {
            lineWidth: props.lineWidth,
          },
        },
        threshold: null,
      },
    },
    credits: {
      enabled: false,
    },
    title: {
      text: null,
    },
    xAxis: {
      type: 'datetime',
      title: undefined,
      crosshair: {
        width: 0,
        height: 50,
      },
      lineWidth: 0,
      minorGridLineWidth: 0,
      tickWidth: 1,
      lineColor: 'transparent',
      tickPixelInterval: isDeviceMobile ? 50 : 100,
      tickPositioner() {
        const xAxis: any = this;
        const tickPositionsInfo = xAxis.tickPositions;
        const ticks = tickPositionsInfo.slice(0, tickPositionsInfo.length);
        if (timeFrame !== '1m' || ticks.length < 10) {
          return;
        }
        const positions = [];
        const tickInterval = Math.floor(
          ticks.length / Math.min(5, ticks.length),
        );

        for (let int = 2; int < ticks.length; int += tickInterval) {
          positions.push(ticks[int]);
        }

        return positions;
      },
      tickLength: 0,
      dateTimeLabelFormats: {
        hour: { main: '%h:%m' },
        day: { main: '%e %b' },
        month: { main: '%b %Y' },
        year: { main: '%Y' },
      },
      labels: {
        useHTML: true,
        align: 'center',
        formatter(label: any) {
          const time = moment(label.value).format('HH:mm');

          const day = moment(label.value).format('ddd');
          const date = moment(label.value).format('D');
          const thisMonth = moment(label.value).format('MMM');

          const month = moment(label.value).format('MMM');
          const monthDay = moment(label.value).format('D');

          const yearMonth = moment(label.value).format('MMM');
          const year = moment(label.value).format('YYYY');

          switch (timeFrame) {
            case '24h':
              return `
                                <span style="
                                    font-weight: bold;
                                    font-size: 14px;
                                    line-height: 17px;
                                    opacity: 0.3;
                                    font-family: Bozon;
                                " class="text-header-text">
                                    ${time}
                                </span>
                            `;
            case '7d':
              return `
                            <div style="display: flex; flex-direction: column; align-items: center">
                                <span
                                    style="
                                        font-weight: bold;
                                        font-size: 18px;
                                        line-height: 17px;
                                        opacity: 0.3;
                                        font-family: Bozon;
                                    " class="text-header-text">
                                    ${date}
                                    <br />
                                </span>
                                 <span
                                    style="
                                        font-weight: regular;
                                        font-size: 14px;
                                        line-height: 17px;
                                        opacity: 0.3;
                                        font-family: Bozon;
                                " class="text-header-text">
                                    ${thisMonth}
                                </span>
                            </div>
                                `;
            case '1m':
              return `
                            <div style="display: flex; flex-direction: column; align-items: center">
                                ${
                                  !isDeviceMobile
                                    ? `<span
                                    style="
                                        font-weight: bold;
                                        font-size: 14px;
                                        line-height: 17px;
                                        opacity: 0.3;
                                        font-family: Bozon;
                                " class="text-header-text">
                                    ${day}
                                    <br />
                                </span>`
                                    : ''
                                }
                                <span
                                    style="
                                        font-weight: bold;
                                        font-size: 14px;
                                        line-height: 17px;
                                        opacity: 0.3;
                                        font-family: Bozon;
                                " class="text-header-text">
                                    ${monthDay}
                                    <br/>
                                </span>
                                <span
                                    style="
                                        font-weight: bold;
                                        font-size: 14px;
                                        line-height: 17px;
                                        opacity: 0.3;
                                        font-family: Bozon;
                                " class="text-header-text">
                                    ${month}
                                </span>
                            </div>
                            `;
            case '3m':
              return `
                            <div style="display: flex; flex-direction: column; align-items: center">
                                <span
                                    style="
                                        font-weight: bold;
                                        font-size: 14px;
                                        line-height: 17px;
                                        opacity: 0.3;
                                        font-family: Bozon;
                                " class="text-header-text">
                                    ${monthDay}
                                    <br/>
                                </span>
                                <span
                                    style="
                                        font-weight: bold;
                                        font-size: 14px;
                                        line-height: 17px;
                                        opacity: 0.3;
                                        font-family: Bozon;
                                " class="text-header-text">
                                    ${month}
                                </span>
                                <span
                                    style="
                                        font-weight: bold;
                                        font-size: 14px;
                                        line-height: 17px;
                                        opacity: 0.3;
                                        font-family: Bozon;
                                " class="text-header-text">
                                    ${year}
                                </span>
                            </div>
                            `;
            case '1y':
            case 'all':
              return `
                            <div style="display: flex; flex-direction: column; align-items: center">
                                <span
                                    style="
                                        font-weight: bold;
                                        font-size: 14px;
                                        line-height: 17px;
                                        opacity: 0.3;
                                        font-family: Bozon;
                                " class="text-header-text">
                                    ${yearMonth}<br/>
                                </span>
                                <span
                                    style="
                                        font-weight: bold;
                                        font-size: 14px;
                                        line-height: 17px;
                                        opacity: 0.3;
                                        font-family: Bozon;
                                " class="text-header-text">
                                    ${year}
                                </span>
                                </div>
                            `;
            default:
              return `<span>${moment.utc(label.value).format('MMM DDD')}</span>`;
          }
        },
      },
    },
    yAxis: [
      {
        align: 'center',
        tickInterval: yInterval,
        max: maxY,
        min: minY,
        floor: minY,
        gridLineWidth: 0,
        opposite: true,
        offset: isDeviceMobile ? -30 : -10,
        title: undefined,
        endOnTick: false,
        minorGridLineWidth: 0,
        plotLines: [
          {
            value:
              primaryData !== undefined && primaryData.length > 0
                ? primaryData[0].change
                : 0,
            color: '#4b5267',
            dashStyle: 'dash',
            width: 2,
            label: {
              align: 'right',
              useHTML: true,
            },
          },
        ],
        labels: {
          align: 'right',
          enabled: true,
          y: !isDeviceMobile && 20,
          formatter(label: any) {
            if (
              label.value < minY ||
              label.value > maxY ||
              (!isDeviceMobile && label.value !== 0)
            )
              return;

            return `
                            <div style="
                                font-weight: bold;
                                font-size: ${isDeviceMobile ? '10px' : '14px'};
                                line-height: 17px;
                                color: #FFFFFF;
                                opacity: 0.3;
                                font-family: Bozon;
                                z-index: 5;
                                transform: translate(0, 20px);"
                            >
                                ${formatNumber(label.value, 0)}%
                            </div>
                        `;
          },
        },
      },
    ],
    series: [
      {
        data:
          primaryData &&
          primaryData.map(p => ({ ...p, x: p.x * 1000, y: p.change })),
        color: graphColor,
        turboThreshold: 100000,
        marker: {
          symbol: 'circle',
          states: {
            hover: {
              lineWidthPlus: props.lineWidth,
              opacity: 1,
            },
            normal: {
              opacity: 0,
            },
          },
          lineColor: graphColor,
          fillColor: graphColor,
        },
        animation: {
          duration: 2000,
        },
        states: {
          hover: {
            lineWidth: props.lineWidth + 2,
          },
        },
        fillColor: {
          linearGradient: {
            x1: 0,
            y1: 0,
            x2: 0,
            y2: 1,
          },
          stops: [
            [0, hexToRgba(graphColor, 54)],
            [1, 'rgba(98, 106, 255, 0)'],
          ],
        },
      },
      {
        name: secondaryData ? 'Compare' : '',
        data:
          secondaryData && secondaryData.map(p => ({ ...p, x: p.x * 1000 })),
        color: secondaryColor,
        marker: {
          symbol: 'circle',
          states: {
            hover: {
              lineWidthPlus: 2,
              opacity: 1,
            },
            normal: {
              opacity: 0,
            },
          },
          lineColor: secondaryColor,
          fillColor: '#fff',
        },
        animation: {
          duration: 2000,
        },
        states: {
          hover: {
            lineWidth: 4,
          },
        },
        fillColor: {
          linearGradient: {
            x1: 0,
            y1: 0,
            x2: 0,
            y2: 1,
          },
          stops: [
            [0, hexToRgba(colors['cornflower-blue'], 54)],
            [1, 'rgba(98, 106, 255, 0)'],
          ],
        },
      },
    ],
    legend: {
      enabled: false,
    },
  };

  return (
    <HighchartsReact
      highcharts={Highcharts}
      options={options}
      className="relative"
    />
  );
};

export default ProductGraph;
